import type { ReactNode } from 'react';
import { cn } from '@strategy4kids/ui';

/** The glass panel every non-game screen sits its content on, so text keeps contrast over the PageArt picture. */
export const PANEL_CLASS = 'rounded-3xl border border-border/60 bg-surface/85 shadow-sm backdrop-blur-md dark:bg-surface/80';

/**
 * A screen heading on a translucent panel: title, optional subtitle, an action (usually Back) on the
 * right, and an optional hero picture that fills the right half from `lg` up. Title and action
 * keep whatever testids the screen passes in.
 */
export function PageHeader({
  title,
  subtitle,
  action,
  hero,
  spot,
  titleTestId,
  titleProps,
  className,
}: {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly action?: ReactNode;
  /** URL of a picture for the right side (decorative). */
  readonly hero?: string;
  /** A small picture beside the title (a phone's only picture). Pass real alt text. */
  readonly spot?: { readonly src: string; readonly alt: string };
  readonly titleTestId?: string;
  /** Extra attributes for the h1 (appdom ids etc.). */
  readonly titleProps?: Record<`data-${string}`, string>;
  readonly className?: string;
}) {
  return (
    <header className={cn('relative flex items-stretch overflow-hidden', PANEL_CLASS, className)}>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-5 py-4 sm:px-8 sm:py-6 lg:px-10 lg:py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {spot ? <img src={spot.src} alt={spot.alt} className="size-16 shrink-0 rounded-2xl object-cover sm:size-20" /> : null}
            <h1 className="min-w-0 font-display text-h1 font-bold tracking-tight text-fg [overflow-wrap:anywhere] lg:text-5xl" data-testid={titleTestId} {...titleProps}>
              {title}
            </h1>
          </div>
          {action}
        </div>
        {subtitle ? <p className="max-w-prose text-base font-semibold text-fg-muted lg:text-lg">{subtitle}</p> : null}
      </div>
      {hero ? (
        <img src={hero} alt="" aria-hidden="true" loading="lazy" className="hidden w-1/3 object-cover lg:block" />
      ) : null}
    </header>
  );
}
